/**
 * THE PORTAL'S OWN REMINDER-RULE MAPPING — a deliberate COPY of the mobile picker's logic.
 *
 * The founder ruled on this directly (2026-09-10), after I had shared the mobile module instead:
 *
 *   *"I do not approve this change. While it seems better and has the likely by many DRY principle,
 *   it results in more coupling and future bugs in multiple places when we want to change only one.
 *   Copying is cheap this day. So just copy it and if in the future they diverge - it is fine, at a
 *   minimum we do not break the other user, or if needed, keep them synchronized."*
 *
 * So this file intentionally duplicates `@cc/mobile/src/utils/esReminderRule.ts` rather than
 * importing it, and the portal takes no dependency on `@cc/domain`. The two are expected to agree
 * TODAY — the founder asked for identical options bar skipping — and are free to diverge later
 * without either surface breaking the other. If you change a rule here, the mobile file is the
 * counterpart to consider; nothing enforces it, by design.
 *
 * WHAT IT COMPOSES: the same whitelisted v1 subset the decider accepts — four frequencies, an
 * interval, weekday chips, a month day, one time, and a bound that is Never, COUNT or UNTIL, never
 * both (RFC 5545 §3.3.10). Free-form RRULE is never exposed: a rule outside the subset is a
 * notification at a time nobody chose, and the decider would refuse it anyway.
 *
 * PURE, and unit-tested without a renderer.
 */

export type ReminderFrequencyChoice =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'oneTime';

export type ReminderBoundChoice = 'never' | 'count' | 'until';

export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
export type ReminderWeekday = (typeof WEEKDAYS)[number];

export type CalendarDate = { year: number; month: number; day: number };

export type ReminderScheduleDraft = {
  frequency: ReminderFrequencyChoice;
  /** `INTERVAL`, ≥ 1. Ignored for `oneTime`, which is a `COUNT=1` rule. */
  interval: number;
  /** WEEKLY only. Empty is invalid — a weekly rule with no day generates nothing. */
  weekdays: ReminderWeekday[];
  /** MONTHLY only, 1..31. */
  monthDay: number;
  hour: number;
  minute: number;
  /** The first day the rule may fire — the "starting" field, a local calendar date. */
  startDate: CalendarDate;
  bound: ReminderBoundChoice;
  /** `COUNT`, ≥ 1. Read only when `bound === 'count'`. */
  count: number;
  /** `UNTIL`, a local calendar date. Read only when `bound === 'until'`. */
  untilDate: CalendarDate;
};

export type PortalSchedule = {
  rrule: string;
  dtstart: string;
  exdates: string[];
};

const pad = (n: number) => String(n).padStart(2, '0');
const dateOf = (at: Date): CalendarDate => ({
  year: at.getFullYear(),
  month: at.getMonth() + 1,
  day: at.getDate(),
});
const asDate = (d: CalendarDate) => new Date(d.year, d.month - 1, d.day);

/** The draft a NEW schedule opens with: daily, 09:00, starting today, unbounded. */
export const defaultDraft = (
  now: Date = new Date(),
): ReminderScheduleDraft => ({
  frequency: 'daily',
  interval: 1,
  weekdays: [WEEKDAYS[(now.getDay() + 6) % 7]],
  monthDay: now.getDate(),
  hour: 9,
  minute: 0,
  startDate: dateOf(now),
  bound: 'never',
  count: 5,
  untilDate: dateOf(new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)),
});

/** Weekday chips render and serialise in calendar order, never in the order they were tapped. */
const ordered = (days: ReminderWeekday[]): ReminderWeekday[] =>
  WEEKDAYS.filter((d) => days.includes(d));

/**
 * DTSTART MUST FALL ON ITS OWN RULE. RFC 5545 requires it, and the decider refuses a mismatch
 * (`REMINDER_DTSTART_MISMATCH`) because the spec leaves that recurrence set UNDEFINED and libraries
 * diverge exactly there. So the anchor is advanced from the chosen start date to the first day the
 * rule can actually fire, rather than being offered to the decider to reject.
 */
const anchorFor = (draft: ReminderScheduleDraft): Date => {
  const from = asDate(draft.startDate);
  if (draft.frequency === 'weekly' && draft.weekdays.length > 0) {
    const wanted = new Set(ordered(draft.weekdays));
    for (let i = 0; i < 7; i += 1) {
      const at = new Date(from.getTime() + i * 86400000);
      if (wanted.has(WEEKDAYS[(at.getDay() + 6) % 7])) return at;
    }
    return from;
  }
  if (draft.frequency === 'monthly') {
    const day = Math.min(31, Math.max(1, Math.trunc(draft.monthDay)));
    const at = new Date(from);
    // walk forward at most a couple of months: a 31st exists only in some of them
    for (let i = 0; i < 70; i += 1) {
      if (at.getDate() === day) return at;
      at.setDate(at.getDate() + 1);
    }
    return from;
  }
  return from;
};

/** The draft → the rule + a matching anchor. `exdates` are carried through untouched. */
export const scheduleOfDraft = (
  draft: ReminderScheduleDraft,
  exdates: string[] = [],
): PortalSchedule => {
  const oneTime = draft.frequency === 'oneTime';
  const interval = oneTime ? 1 : Math.max(1, Math.trunc(draft.interval));
  const freq =
    draft.frequency === 'weekly'
      ? 'WEEKLY'
      : draft.frequency === 'monthly'
      ? 'MONTHLY'
      : 'DAILY';

  const parts = [`FREQ=${freq}`];
  if (interval > 1) parts.push(`INTERVAL=${interval}`);
  if (draft.frequency === 'weekly' && draft.weekdays.length > 0) {
    parts.push(`BYDAY=${ordered(draft.weekdays).join(',')}`);
  }
  if (draft.frequency === 'monthly') {
    parts.push(
      `BYMONTHDAY=${Math.min(31, Math.max(1, Math.trunc(draft.monthDay)))}`,
    );
  }
  parts.push(`BYHOUR=${draft.hour}`, `BYMINUTE=${draft.minute}`);

  // COUNT and UNTIL are MUTUALLY EXCLUSIVE. A one-time reminder is `COUNT=1` — the same shape the
  // assessment re-take converts to, which is why the kernel needs no separate one-shot vocabulary.
  if (oneTime) {
    parts.push('COUNT=1');
  } else if (draft.bound === 'count') {
    parts.push(`COUNT=${Math.max(1, Math.trunc(draft.count))}`);
  } else if (draft.bound === 'until') {
    const u = asDate(draft.untilDate);
    parts.push(
      `UNTIL=${u.getFullYear()}${pad(u.getMonth() + 1)}${pad(
        u.getDate(),
      )}T235959Z`,
    );
  }

  const at = anchorFor(draft);
  const dtstart = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(
    at.getDate(),
  )}T${pad(draft.hour)}:${pad(draft.minute)}:00`;

  return { rrule: parts.join(';'), dtstart, exdates };
};

const part = (rrule: string, key: string) => {
  const m = new RegExp(`(?:^|;)${key}=([^;]*)`).exec(rrule || '');
  return m ? m[1] : '';
};

/** A stored rule → the form's state, so an edit opens on what the client actually has. */
export const draftOfSchedule = (
  schedule: { rrule: string; dtstart: string },
  now: Date = new Date(),
): ReminderScheduleDraft => {
  const base = defaultDraft(now);
  const rrule = schedule?.rrule ?? '';
  if (!rrule) return base;

  const freq = part(rrule, 'FREQ');
  const count = part(rrule, 'COUNT');
  const until = part(rrule, 'UNTIL');
  const oneTime = count === '1';

  const start = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(
    schedule?.dtstart ?? '',
  );
  const startDate = start
    ? { year: +start[1], month: +start[2], day: +start[3] }
    : base.startDate;

  const untilMatch = /^(\d{4})(\d{2})(\d{2})/.exec(until);

  return {
    frequency: oneTime
      ? 'oneTime'
      : freq === 'WEEKLY'
      ? 'weekly'
      : freq === 'MONTHLY'
      ? 'monthly'
      : 'daily',
    interval: Math.max(1, Number(part(rrule, 'INTERVAL') || 1)),
    weekdays: part(rrule, 'BYDAY')
      .split(',')
      .map((d) => d.trim())
      .filter((d): d is ReminderWeekday =>
        (WEEKDAYS as readonly string[]).includes(d),
      ),
    monthDay: Number(part(rrule, 'BYMONTHDAY') || startDate.day),
    hour: Number(part(rrule, 'BYHOUR') || (start ? +start[4] : base.hour)),
    minute: Number(
      part(rrule, 'BYMINUTE') || (start ? +start[5] : base.minute),
    ),
    startDate,
    bound: oneTime ? 'never' : count ? 'count' : until ? 'until' : 'never',
    count: !oneTime && count ? Number(count) : base.count,
    untilDate: untilMatch
      ? {
          year: +untilMatch[1],
          month: +untilMatch[2],
          day: +untilMatch[3],
        }
      : base.untilDate,
  };
};

export type DraftProblem = 'WEEKDAY_REQUIRED' | 'END_BEFORE_START';

/**
 * The form's own gate. It is not a substitute for the decider — a client is not a trust boundary —
 * it exists so a person cannot compose something that will be refused only after they press save.
 */
export const draftProblem = (
  draft: ReminderScheduleDraft,
): DraftProblem | null => {
  if (draft.frequency === 'weekly' && draft.weekdays.length === 0) {
    return 'WEEKDAY_REQUIRED';
  }
  if (
    draft.frequency !== 'oneTime' &&
    draft.bound === 'until' &&
    asDate(draft.untilDate) < asDate(draft.startDate)
  ) {
    // saying THIS rather than a generic refusal matters: a generic one sent the founder looking at
    // the repeat pattern, which was never the problem (R50)
    return 'END_BEFORE_START';
  }
  return null;
};

/** Which `reminders.summary.*` key describes a rule, and the values it interpolates. */
export const summaryOf = (
  rrule: string,
  /** DTSTART — a ONE-TIME rule's day is its anchor's (the sentence names it, 2026-09-28 fix). */
  dtstart = '',
): { key: string; values: Record<string, string | number> } => {
  const d = draftOfSchedule({ rrule, dtstart });
  const time = `${pad(d.hour)}:${pad(d.minute)}`;
  // bare, comma-separated RRULE codes: this module stays free of i18n, and the surface localises
  // them before they reach a sentence (see `describe` in index.tsx)
  const days = ordered(d.weekdays).join(',');
  if (d.frequency === 'oneTime') {
    // `YYYY-MM-DD` of the anchor's wall clock — the surface renders it in the reader's language
    const day = /^(\d{4}-\d{2}-\d{2})T/.exec(dtstart)?.[1];
    return { key: 'oneTime', values: day ? { time, date: day } : { time } };
  }
  if (d.frequency === 'weekly') {
    return d.interval > 1
      ? { key: 'everyNWeeks', values: { interval: d.interval, days, time } }
      : { key: 'weekly', values: { days, time } };
  }
  if (d.frequency === 'monthly') {
    return d.interval > 1
      ? { key: 'everyNMonths', values: { interval: d.interval, time } }
      : { key: 'monthlyOnDay', values: { day: d.monthDay, time } };
  }
  return d.interval > 1
    ? { key: 'everyNDays', values: { interval: d.interval, time } }
    : { key: 'daily', values: { time } };
};
